import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip
} from '@mui/material';
import { 
  Save, 
  Cancel, 
  Add,
  Edit,
  Delete,
  Event,
  AccessTime,
  LocationOn
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { format } from 'date-fns';
import { 
  Section, 
  ProgramacaoSection, 
  ProgramacaoEvent,
  generateEventId 
} from '../../types/festejo';

interface ProgramacaoSectionEditorProps {
  section: Section | null;
  onSave: (section: Section) => void;
  onCancel: () => void;
}

interface FormData {
  title: string;
  events: ProgramacaoEvent[];
}

interface EventFormData {
  title: string;
  dateTime: string;
  location: string;
  description: string;
}

interface FormErrors {
  title?: string;
  events?: string;
}

interface EventErrors {
  title?: string;
  dateTime?: string;
}

const ProgramacaoSectionEditor: React.FC<ProgramacaoSectionEditorProps> = ({
  section,
  onSave,
  onCancel
}) => {
  const [formData, setFormData] = useState<FormData>({
    title: '',
    events: []
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [eventDialogOpen, setEventDialogOpen] = useState<boolean>(false);
  const [editingEvent, setEditingEvent] = useState<ProgramacaoEvent | null>(null);
  const [eventFormData, setEventFormData] = useState<EventFormData>({
    title: '',
    dateTime: '',
    location: '',
    description: ''
  });
  const [eventErrors, setEventErrors] = useState<EventErrors>({});

  // Carregar dados da seção se estiver editando
  useEffect(() => {
    if (section && section.type === 'programacao') {
      const programacaoSection = section as ProgramacaoSection;
      setFormData({
        title: programacaoSection.title,
        events: [...programacaoSection.events].sort((a, b) => 
          new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
        )
      });
    }
  }, [section]);

  // Validar formulário principal
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Título deve ter pelo menos 3 caracteres';
    }

    if (formData.events.length === 0) {
      newErrors.events = 'Adicione pelo menos um evento à programação';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validar formulário de evento
  const validateEventForm = (): boolean => {
    const newErrors: EventErrors = {};

    if (!eventFormData.title.trim()) {
      newErrors.title = 'Título do evento é obrigatório';
    }

    if (!eventFormData.dateTime) {
      newErrors.dateTime = 'Data e hora são obrigatórias';
    }

    setEventErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manipular mudanças no título
  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, title: value }));

    if (errors.title) {
      setErrors(prev => ({ ...prev, title: undefined }));
    }
  };

  // Abrir dialog para adicionar evento
  const handleAddEvent = () => {
    setEditingEvent(null);
    setEventFormData({
      title: '',
      dateTime: '',
      location: '',
      description: ''
    });
    setEventErrors({});
    setEventDialogOpen(true);
  };

  // Abrir dialog para editar evento
  const handleEditEvent = (event: ProgramacaoEvent) => {
    setEditingEvent(event);
    setEventFormData({
      title: event.title,
      dateTime: event.dateTime,
      location: event.location || '',
      description: event.description || ''
    });
    setEventErrors({});
    setEventDialogOpen(true);
  };

  // Fechar dialog de evento
  const handleCloseEventDialog = () => {
    setEventDialogOpen(false);
    setEditingEvent(null);
    setEventFormData({
      title: '',
      dateTime: '',
      location: '',
      description: ''
    });
    setEventErrors({});
  };

  // Manipular mudanças no formulário de evento
  const handleEventFieldChange = (field: keyof EventFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setEventFormData(prev => ({ ...prev, [field]: value }));

    if (eventErrors[field as keyof EventErrors]) {
      setEventErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Manipular mudança de data/hora
  const handleDateTimeChange = (date: Date | null) => {
    if (date) {
      setEventFormData(prev => ({ ...prev, dateTime: date.toISOString() }));
      
      if (eventErrors.dateTime) {
        setEventErrors(prev => ({ ...prev, dateTime: undefined }));
      }
    }
  };

  // Salvar evento
  const handleSaveEvent = () => {
    if (!validateEventForm()) return;

    const newEvent: ProgramacaoEvent = {
      id: editingEvent?.id || generateEventId(),
      title: eventFormData.title.trim(),
      dateTime: eventFormData.dateTime,
      location: eventFormData.location.trim() || undefined,
      description: eventFormData.description.trim() || undefined
    };

    let updatedEvents: ProgramacaoEvent[];

    if (editingEvent) {
      // Editando evento existente
      updatedEvents = formData.events.map(event =>
        event.id === editingEvent.id ? newEvent : event
      );
    } else {
      // Adicionando novo evento
      updatedEvents = [...formData.events, newEvent];
    }

    // Ordenar por data
    updatedEvents.sort((a, b) => 
      new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
    );

    setFormData(prev => ({ ...prev, events: updatedEvents }));
    
    // Limpar erro de eventos se existir
    if (errors.events) {
      setErrors(prev => ({ ...prev, events: undefined }));
    }

    handleCloseEventDialog();
  };

  // Excluir evento
  const handleDeleteEvent = (eventId: string) => {
    const updatedEvents = formData.events.filter(event => event.id !== eventId);
    setFormData(prev => ({ ...prev, events: updatedEvents }));
  };

  // Salvar seção
  const handleSave = () => {
    if (!validateForm()) return;

    const programacaoSection: ProgramacaoSection = {
      id: section?.id || '',
      type: 'programacao',
      title: formData.title.trim(),
      events: formData.events,
      order: section?.order || 0
    };

    onSave(programacaoSection);
  };

  // Formatar data para exibição
  const formatEventDateTime = (dateTime: string): string => {
    try {
      const date = new Date(dateTime);
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Use seções de programação para listar todos os eventos do festejo, como missas, 
          procissões, apresentações e atividades especiais.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Título da Programação"
            value={formData.title}
            onChange={handleTitleChange}
            error={!!errors.title}
            helperText={errors.title || 'Ex: Programação da Festa de São João'}
            required
            sx={{ mb: 3 }}
          />

          {/* Lista de eventos */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Eventos ({formData.events.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddEvent}
            >
              Adicionar Evento
            </Button>
          </Box>

          {formData.events.length === 0 ? (
            <Alert severity="info">
              Nenhum evento adicionado ainda. Clique em "Adicionar Evento" para começar.
            </Alert>
          ) : (
            <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
              <List>
                {formData.events.map((event, index) => (
                  <ListItem key={event.id} divider={index < formData.events.length - 1}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" component="span">
                            {event.title}
                          </Typography>
                          <Chip 
                            icon={<AccessTime />}
                            label={formatEventDateTime(event.dateTime)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          {event.location && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <LocationOn fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {event.location}
                              </Typography>
                            </Box>
                          )}
                          {event.description && (
                            <Typography variant="body2" color="text.secondary">
                              {event.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        onClick={() => handleEditEvent(event)}
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDeleteEvent(event.id)}
                        size="small"
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {errors.events && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {errors.events}
            </Typography>
          )}
        </Box>

        {/* Dicas */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>💡 Dicas para programação:</strong><br/>
            • Inclua horários precisos para cada evento<br/>
            • Adicione locais específicos quando diferentes da igreja principal<br/>
            • Use descrições para explicar eventos especiais<br/>
            • Os eventos serão ordenados automaticamente por data e hora
          </Typography>
        </Alert>

        {/* Botões */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            startIcon={<Cancel />}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={<Save />}
          >
            Salvar Seção
          </Button>
        </Box>

        {/* Dialog para adicionar/editar evento */}
        <Dialog
          open={eventDialogOpen}
          onClose={handleCloseEventDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingEvent ? 'Editar Evento' : 'Adicionar Evento'}
          </DialogTitle>
          
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Título do Evento"
                  value={eventFormData.title}
                  onChange={handleEventFieldChange('title')}
                  error={!!eventErrors.title}
                  helperText={eventErrors.title || 'Ex: Missa Solene de Abertura'}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <DateTimePicker
                  label="Data e Hora"
                  value={eventFormData.dateTime ? new Date(eventFormData.dateTime) : null}
                  onChange={handleDateTimeChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!eventErrors.dateTime,
                      helperText: eventErrors.dateTime,
                      required: true
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Local (opcional)"
                  value={eventFormData.location}
                  onChange={handleEventFieldChange('location')}
                  helperText="Ex: Igreja Matriz, Salão Paroquial, Pátio da Igreja"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Descrição (opcional)"
                  value={eventFormData.description}
                  onChange={handleEventFieldChange('description')}
                  helperText="Informações adicionais sobre o evento"
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseEventDialog}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveEvent}
              variant="contained"
            >
              {editingEvent ? 'Salvar Alterações' : 'Adicionar Evento'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ProgramacaoSectionEditor;

